import React, { useState, useEffect } from 'react';
import logo from '../image/logo_alo.png';

const MaintenancePage: React.FC = () => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(30);

  // Détection du mobile et effet de progression
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Listen for resize
    window.addEventListener('resize', checkMobile);
    
    // Animation de progression
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) return 30;
        return prev + 5;
      });
    }, 1500);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearInterval(interval);
    };
  }, []);

  return (
    <div 
      className="maintenance-container"
      style={{
        minHeight: '100vh',
        background: '#4bb0c0',
        backgroundImage: 'linear-gradient(135deg, #4bb0c0 0%, #3a8a9a 100%)',
        fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      {/* Effets d'arrière-plan animés */}
      <div 
        className="bg-effect-1"
        style={{
          position: 'absolute',
          top: '-30%',
          left: '-30%',
          right: '-30%',
          bottom: '-30%',
          background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.15) 0%, transparent 50%)',
          animation: 'moveBackground 25s infinite linear',
          opacity: 0.7
        }}
      />
      
      <div 
        className="bg-effect-2"
        style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: isMobile ? '100px' : '200px',
          height: isMobile ? '100px' : '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 4s infinite ease-in-out',
          opacity: 0.4
        }}
      />
      
      <div 
        className="bg-effect-3"
        style={{
          position: 'absolute',
          bottom: '15%',
          left: '10%',
          width: isMobile ? '80px' : '150px',
          height: isMobile ? '80px' : '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 5s infinite ease-in-out 1s',
          opacity: 0.3
        }}
      />

      {/* Carte principale */}
      <div 
        className="maintenance-card"
        style={{
          textAlign: 'center',
          maxWidth: isMobile ? '90%' : '500px',
          width: '100%',
          padding: isMobile ? '30px 20px' : '50px 40px',
          backgroundColor: 'rgba(255, 255, 255, 0.97)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: isMobile ? '20px' : '24px',
          boxShadow: `
            0 10px 30px rgba(0, 0, 0, 0.1),
            0 1px 8px rgba(0, 0, 0, 0.05),
            inset 0 0 0 1px rgba(255, 255, 255, 0.5)
          `,
          border: '1px solid rgba(255, 255, 255, 0.4)',
          position: 'relative',
          zIndex: 2,
          transition: 'all 0.3s ease'
        }}
      >
        {/* Logo */}
        <div 
          className="logo-container"
          style={{
            width: isMobile ? '140px' : '180px',
            height: isMobile ? '70px' : '90px',
            margin: '0 auto 25px',
            backgroundColor: '#4bb0c0',
            borderRadius: isMobile ? '12px' : '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid  #ffffff',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(75, 176, 192, 0.2)'
          }}
        >
          <img 
            src={logo} 
            alt="Logo ALO" 
            style={{ 
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center'
            }} 
          />
        </div>
        
        {/* Icône principale */}
        <div 
          className="main-icon"
          style={{
            fontSize: isMobile ? '48px' : '64px',
            marginBottom: isMobile ? '20px' : '24px',
            animation: 'float 3s ease-in-out infinite',
            display: 'inline-block',
            lineHeight: 1
          }}
        >
          🚀
        </div>
        
        {/* Titre */}
        <h1 
          style={{
            fontSize: isMobile ? '1.8rem' : '2.5rem',
            fontWeight: 700,
            marginBottom: '16px',
            color: '#F4A261',
            lineHeight: 1.2,
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          Bientôt en ligne !
        </h1>
        
        {/* Barre de séparation */}
        <div 
          style={{
            width: isMobile ? '60px' : '80px',
            height: '4px',
            background: 'linear-gradient(90deg, #4bb0c0, #f4a261)',
            margin: '0 auto 25px',
            borderRadius: '2px',
            opacity: 0.8
          }}
        />
        
        {/* Message */}
        <div 
          style={{
            color: '#080606ff',
            fontSize: isMobile ? '1rem' : '1.2rem',
            lineHeight: 1.6,
            marginBottom: isMobile ? '30px' : '40px',
            padding: isMobile ? '0' : '0 10px'
          }}
        >
          <p style={{ marginBottom: '15px' }}>
            Nous effectuons actuellement des améliorations pour vous offrir une meilleure expérience.
          </p>
          <p 
            style={{
              color: '#e96e10',
              fontWeight: 600,
              fontSize: isMobile ? '1.1rem' : '1.2rem',
              margin: 0,
              textShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            Merci de votre patience.
          </p>
        </div>
        
        {/* Indicateur de progression */}
        <div style={{ marginBottom: '30px' }}>
          <div 
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: 'rgba(75, 176, 192, 0.15)',
              borderRadius: '4px',
              overflow: 'hidden',
              marginBottom: '12px',
              border: '1px solid rgba(75, 176, 192, 0.1)'
            }}
          >
            <div 
              style={{
                width: `${progress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #4bb0c0, #f4a261)',
                borderRadius: '4px',
                transition: 'width 0.8s ease',
                boxShadow: '0 0 10px rgba(244, 162, 97, 0.3)'
              }}
            />
          </div>
          <div 
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              color: '#777',
              fontWeight: 500
            }}
          >
            <span>Chargement...</span>
            <span>{progress}%</span>
          </div>
        </div>
        
        {/* Message de contact pour mobile */}
        {isMobile && (
          <div 
            style={{
              marginTop: '25px',
              padding: '15px',
              backgroundColor: 'rgba(75, 176, 192, 0.1)',
              borderRadius: '12px',
              fontSize: '0.9rem',
              color: '#4a5568',
              border: '1px solid rgba(75, 176, 192, 0.2)'
            }}
          >
            <strong>Besoin d'aide ?</strong>
            <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem' }}>
              Contactez-nous par email :<br />
              <a 
                href="mailto:support@annonce-legale-officielle.fr"
                style={{
                  color: '#e96e10',
                  textDecoration: 'none',
                  fontWeight: 600
                }}
              >
                support@annonce-legale-officielle.fr
              </a>
            </p>
          </div>
        )}

        {/* Styles CSS intégrés avec support mobile */}
        <style>{`
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px); 
            }
            33% { 
              transform: translateY(-12px); 
            }
            66% { 
              transform: translateY(-6px); 
            }
          }
          
          @keyframes pulse {
            0%, 100% { 
              transform: scale(1); 
              opacity: 0.4;
            }
            50% { 
              transform: scale(${isMobile ? '1.05' : '1.1'}); 
              opacity: 0.3;
            }
          }
          
          @keyframes moveBackground {
            0% { 
              transform: translate(0, 0) rotate(0deg); 
            }
            100% { 
              transform: translate(30px, 30px) rotate(360deg); 
            }
          }
          
          /* Améliorations pour très petits écrans */
          @media (max-width: 320px) {
            .maintenance-card {
              padding: 25px 15px !important;
              max-width: 95% !important;
            }
            
            h1 {
              font-size: 1.6rem !important;
            }
            
            .logo-container {
              width: 120px !important;
              height: 60px !important;
            }
            
            .main-icon {
              font-size: 40px !important;
            }
          }
          
          /* Support pour orientation paysage sur mobile */
          @media (max-height: 500px) and (orientation: landscape) {
            .maintenance-container {
              padding: 10px !important;
              min-height: 100vh;
              display: flex;
              align-items: flex-start;
            }
            
            .maintenance-card {
              margin-top: 20px;
              margin-bottom: 20px;
              max-width: 90% !important;
              padding: 25px !important;
            }
            
            .logo-container {
              width: 100px !important;
              height: 50px !important;
              margin-bottom: 15px !important;
            }
          }
          
          /* Améliorations de l'accessibilité */
          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }
          
          /* Support pour le mode somme */
          @media (prefers-color-scheme: dark) {
            .maintenance-card {
              background-color:white !important;
              color: #e2e8f0 !important;
            }
            
            p {
              color: #cbd5e0 !important;
            }
          }
          
          /* Empêcher le zoom sur iOS */
          @supports (-webkit-touch-callout: none) {
            input, textarea, .maintenance-card {
              font-size: 16px !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default MaintenancePage;